import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/apiClient';
import { theme } from '../../styles/theme';
import Button from '../ui/Button';
import BackButton from '../ui/BackButton';
import Card from '../ui/Card';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { formatSqft } from '../../utils/sizeEstimate';

// Orders not charged or invoiced yet, whose estimated prices can still change.
const NOT_YET_BILLED = ['pending', 'checkout_incomplete', 'awaiting_payment', 'awaiting_invoice', 'card_on_file'];

// Size note for an order line whose pieces vary in size (hides).
const SizeLine = ({ item, orderStatus }) => {
  if (!item.approx_sqft) return null;
  let text = `About ${formatSqft(item.approx_sqft)} sq ft each`;
  if (item.actual_sqft) {
    text = `Measured size: ${formatSqft(item.actual_sqft)} sq ft`;
  } else if (NOT_YET_BILLED.includes(orderStatus)) {
    text = `Estimated price · about ${formatSqft(item.approx_sqft)} sq ft each. Your final price is set once we measure your pieces.`;
  }
  return (
    <div style={{
      fontSize: theme.typography.fontSize.xs,
      color: '#92400E',
      marginTop: theme.spacing.xs,
      lineHeight: 1.4,
    }}>
      {text}
    </div>
  );
};

const DashboardOrders = ({ showSuccess, showError }) => {
  const navigate = useNavigate();
  const { isApproved } = useAuth();
  const { openCart, refreshCart } = useCart();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [error, setError] = useState(null);

  // Styles
  const styles = {
    container: {
      width: '100%',
    },
    sectionHeader: {
      marginBottom: theme.spacing.xl,
      paddingBottom: theme.spacing.lg,
      borderBottom: `2px solid ${theme.colors.borderLight}`,
    },
    sectionTitle: {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize['3xl'],
      color: '#8B4513',
      marginBottom: 0,
    },
    errorBox: {
      padding: theme.spacing.lg,
      backgroundColor: '#FEF2F2',
      color: '#991B1B',
      borderRadius: theme.borderRadius.md,
      marginBottom: theme.spacing.xl,
      border: '1px solid #FEE2E2',
      fontFamily: theme.typography.fontFamily.secondary,
    },
    emptyState: {
      textAlign: 'center',
      padding: `${theme.spacing.xxl} ${theme.spacing.lg}`,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      boxShadow: theme.shadows.sm,
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: theme.spacing.lg,
    },
    orderCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      boxShadow: theme.shadows.md,
      padding: theme.spacing.lg,
      cursor: 'pointer',
      transition: theme.transitions.base,
      border: `1px solid ${theme.colors.borderLight}`,
    },
    orderCardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: theme.spacing.md,
      gap: theme.spacing.sm,
      flexWrap: 'wrap',
    },
    orderIdGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.xs,
    },
    orderId: {
      fontFamily: 'monospace',
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
    },
    orderDate: {
      fontSize: theme.typography.fontSize.xs,
      color: theme.colors.textLight,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    statusBadge: {
      padding: `${theme.spacing.xs} ${theme.spacing.md}`,
      borderRadius: theme.borderRadius.sm,
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.semibold,
      textTransform: 'capitalize',
      whiteSpace: 'nowrap',
    },
    orderCardBody: {
      display: 'flex',
      gap: theme.spacing.xl,
      marginBottom: theme.spacing.lg,
      paddingBottom: theme.spacing.lg,
      borderBottom: `1px solid ${theme.colors.borderLight}`,
    },
    orderStat: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.xs,
    },
    orderStatLabel: {
      fontSize: theme.typography.fontSize.xs,
      color: theme.colors.textLight,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontFamily: theme.typography.fontFamily.secondary,
    },
    orderStatValue: {
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
    },
    orderCardFooter: {
      display: 'flex',
      gap: theme.spacing.sm,
      flexWrap: 'wrap',
    },
    orderBtn: {
      flex: 1,
      minWidth: '120px',
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      border: 'none',
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      cursor: 'pointer',
      transition: theme.transitions.base,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    orderBtnDetails: {
      backgroundColor: theme.colors.surface,
      color: theme.colors.text,
      border: `1px solid ${theme.colors.border}`,
    },
    orderBtnReorder: {
      backgroundColor: theme.colors.primary,
      color: theme.colors.textOnDark,
    },
    detailView: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xl,
      boxShadow: theme.shadows.md,
    },
    detailHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: theme.spacing.xl,
      gap: theme.spacing.md,
      flexWrap: 'wrap',
    },
    detailTitle: {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize['3xl'],
      color: '#8B4513',
      marginBottom: theme.spacing.xs,
    },
    detailSubtitle: {
      color: theme.colors.textLight,
      fontSize: theme.typography.fontSize.sm,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    tableContainer: {
      overflowX: 'auto',
      border: `1px solid ${theme.colors.borderLight}`,
      borderRadius: theme.borderRadius.lg,
      WebkitOverflowScrolling: 'touch',
      marginBottom: theme.spacing.xl,
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '600px',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
      gap: theme.spacing.xl,
    },
    infoCard: {
      background: '#f9fafb',
      padding: theme.spacing.lg,
      borderRadius: theme.borderRadius.lg,
      border: `1px solid ${theme.colors.borderLight}`,
    },
    infoCardTitle: {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.xl,
      marginBottom: theme.spacing.lg,
      color: '#8B4513',
    },
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.users.getOrders();
      setOrders(data || []);
    } catch (err) {
      setError('Failed to load orders: ' + (err.response?.data?.detail || err.message));
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (orderId) => {
    try {
      setLoading(true);
      const response = await api.orders.reorder(orderId);
      await refreshCart();
      showSuccess(response.message || 'Items added to your cart!', 'Reorder Successful');
      setTimeout(() => openCart(), 500);
    } catch (err) {
      showError('Failed to reorder: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'status-pending',
      checkout_incomplete: 'status-pending',
      awaiting_payment: 'status-pending',
      paid: 'status-paid',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-paid',
      cancelled: 'status-cancelled',
    };
    return colors[status?.toLowerCase()?.replace(/ /g, '_')] || 'status-pending';
  };

  const getStatusBgColor = (status) => {
    const colors = {
      pending: '#FEF3C7',
      checkout_incomplete: '#FFEDD5',
      awaiting_payment: '#FEF3C7',
      paid: '#D1FAE5',
      processing: '#DBEAFE',
      shipped: '#E0E7FF',
      delivered: '#D1FAE5',
      cancelled: '#FEE2E2',
    };
    return colors[status?.toLowerCase()?.replace(/ /g, '_')] || '#FEF3C7';
  };

  const getStatusTextColor = (status) => {
    const colors = {
      pending: '#92400E',
      checkout_incomplete: '#C2410C',
      awaiting_payment: '#92400E',
      paid: '#065F46',
      processing: '#1E40AF',
      shipped: '#3730A3',
      delivered: '#065F46',
      cancelled: '#991B1B',
    };
    return colors[status?.toLowerCase()?.replace(/ /g, '_')] || '#92400E';
  };

  if (loading && !orders.length) {
    return (
      <div style={{ textAlign: 'center', padding: '5rem 0' }}>
        <div style={{ 
          display: 'inline-block', 
          width: '40px', 
          height: '40px', 
          border: '3px solid #f3f3f3', 
          borderTop: '3px solid #CC5500', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite',
          marginBottom: '1rem'
        }} />
        <p style={{ color: '#666', fontFamily: theme.typography.fontFamily.secondary }}>Loading your orders...</p>
      </div>
    );
  }

  if (!isApproved) {
    return (
      <Card variant="parchment" padding="xl">
        <h3 style={{ fontFamily: theme.typography.fontFamily.primary, color: theme.colors.text, marginBottom: theme.spacing.md }}>
          Account Pending Approval
        </h3>
        <p style={{ color: theme.colors.textSecondary }}>
          Your account is currently pending approval. Once approved, you'll be able to view and place orders.
        </p>
      </Card>
    );
  }

  const responsiveStyles = `
    @media (max-width: 768px) {
      .responsive-grid {
        grid-template-columns: 1fr !important;
        gap: ${theme.spacing.md} !important;
      }
      
      .responsive-detail-view {
        padding: ${theme.spacing.md} !important;
      }
      
      .responsive-section-title {
        font-size: ${theme.typography.fontSize['2xl']} !important;
      }
      
      .responsive-table-container {
        border-radius: ${theme.borderRadius.md} !important;
      }
      
      .responsive-order-card {
        padding: ${theme.spacing.md} !important;
      }
      
      .responsive-info-card {
        padding: ${theme.spacing.md} !important;
      }
      
      .responsive-btn-group {
        flex-direction: column;
      }
      
      .responsive-btn-group button {
        width: 100%;
      }
      
      .responsive-detail-header {
        flex-direction: column !important;
        align-items: flex-start !important;
      }
      
      .responsive-detail-header > div {
        width: 100%;
      }
      
      .responsive-detail-header .status-badge {
        align-self: flex-start;
      }
      
      .mobile-order-table {
        display: none !important;
      }
      
      .mobile-order-cards {
        display: block !important;
      }
      
      .invoice-section {
        padding: ${theme.spacing.md} !important;
      }
      
      .invoice-section h3 {
        font-size: ${theme.typography.fontSize.md} !important;
      }
      
      .invoice-section p {
        font-size: ${theme.typography.fontSize.sm} !important;
      }
    }
    
    @media (max-width: 480px) {
      .responsive-section-title {
        font-size: ${theme.typography.fontSize.xl} !important;
      }
      
      .responsive-detail-title {
        font-size: ${theme.typography.fontSize['2xl']} !important;
      }
      
      .responsive-detail-view {
        padding: ${theme.spacing.sm} !important;
      }
      
      .responsive-info-card {
        padding: ${theme.spacing.sm} !important;
      }
    }
    
    @media (min-width: 769px) {
      .mobile-order-cards {
        display: none !important;
      }
    }
  `;

  if (selectedOrder) {
    return (
      <>
        <style>{responsiveStyles}</style>
        <div className="responsive-detail-view" style={styles.detailView}>
          <BackButton 
            label="Back to Orders"
            onClick={() => setSelectedOrder(null)}
            variant="default"
            size="md"
            style={{ marginBottom: theme.spacing.xl }}
          />

          <div className="responsive-detail-header" style={styles.detailHeader}>
            <div>
              <h2 className="responsive-detail-title" style={styles.detailTitle}>Order #{selectedOrder.id}</h2>
              <p style={styles.detailSubtitle}>
                Placed on {new Date(selectedOrder.created_at).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <span 
              className="status-badge"
              style={{
                ...styles.statusBadge,
                backgroundColor: getStatusBgColor(selectedOrder.status),
                color: getStatusTextColor(selectedOrder.status),
              }}
            >
              {selectedOrder.status?.replace(/_/g, ' ')}
            </span>
          </div>

        {/* Checkout Incomplete Warning */}
        {selectedOrder.status === 'checkout_incomplete' && (
          <div style={{
            backgroundColor: '#FFF7ED',
            border: '2px solid #F97316',
            borderRadius: '12px',
            padding: '1.25rem 1.5rem',
            marginBottom: '2rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#C2410C', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Payment Details Incomplete
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#9A3412', margin: 0, lineHeight: 1.5 }}>
              Your order was created but the payment details could not be saved — this usually happens when the card number or expiry entered was incorrect. Please return to checkout and re-enter your card details to complete this order.
            </p>
            <Button
              size="sm"
              onClick={() => navigate('/checkout')}
              style={{ alignSelf: 'flex-start', backgroundColor: '#C2410C', borderColor: '#C2410C' }}
            >
              Return to Checkout
            </Button>
          </div>
        )}

        {/* Invoice Section */}
        {selectedOrder.payment_provider === 'invoice' && (selectedOrder.invoice_generated || selectedOrder.invoice_url) && (
          <div className="invoice-section" style={{
            backgroundColor: '#F0F9FF',
            border: `1px solid #BAE6FD`,
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2.5rem',
          }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0369A1' }}>
              Invoice Generated - {selectedOrder.payment_terms === 'net10' ? 'Net 10' : 'Net 30'} Terms
            </h3>
            <p style={{ fontSize: '0.9rem', color: '#0C4A6E', marginBottom: '1rem' }}>
              Payment is due within {selectedOrder.payment_terms === 'net10' ? '10' : '30'} days of the invoice date.
            </p>
            {selectedOrder.invoice_url && (
              <Button size="sm" onClick={() => window.open(selectedOrder.invoice_url, '_blank')}>
                View Invoice Document
              </Button>
            )}
          </div>
        )}

        {/* Order Items */}
        <div style={{ marginBottom: theme.spacing.xxl }}>
          <h3 style={styles.infoCardTitle}>Items in Order</h3>
          
          {/* Desktop Table View */}
          <div className="mobile-order-table responsive-table-container" style={styles.tableContainer}>
            <table style={styles.table}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  <th style={{ padding: theme.spacing.md, textAlign: 'left', borderBottom: `1px solid ${theme.colors.borderLight}`, fontSize: theme.typography.fontSize.xs, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', fontFamily: theme.typography.fontFamily.secondary }}>Product</th>
                  <th style={{ padding: theme.spacing.md, textAlign: 'left', borderBottom: `1px solid ${theme.colors.borderLight}`, fontSize: theme.typography.fontSize.xs, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', fontFamily: theme.typography.fontFamily.secondary }}>SKU</th>
                  <th style={{ padding: theme.spacing.md, textAlign: 'center', borderBottom: `1px solid ${theme.colors.borderLight}`, fontSize: theme.typography.fontSize.xs, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', fontFamily: theme.typography.fontFamily.secondary }}>Qty</th>
                  <th style={{ padding: theme.spacing.md, textAlign: 'right', borderBottom: `1px solid ${theme.colors.borderLight}`, fontSize: theme.typography.fontSize.xs, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', fontFamily: theme.typography.fontFamily.secondary }}>Price</th>
                  <th style={{ padding: theme.spacing.md, textAlign: 'right', borderBottom: `1px solid ${theme.colors.borderLight}`, fontSize: theme.typography.fontSize.xs, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', fontFamily: theme.typography.fontFamily.secondary }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {selectedOrder.items?.map((item, index) => {
                  const currentPrice = Number(item.unit_price) || 0;
                  const basePrice = Number(item.base_price_without_discount) || currentPrice;
                  const hasDiscount = !item.actual_sqft && Math.abs(currentPrice - basePrice) > 0.01; // measured hides are priced by size, not discounted
                  const wasOnSale = item.was_on_sale === true;
                  
                  return (
                    <tr key={index} style={{ borderBottom: index === selectedOrder.items.length - 1 ? 'none' : `1px solid ${theme.colors.surfaceAlt}` }}>
                      <td style={{ padding: `${theme.spacing.lg} ${theme.spacing.md}`, minWidth: '200px' }}>
                        <div style={{ fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text }}>{item.name}</div>
                        {item.variant_name && (
                          <div style={{ 
                            fontSize: theme.typography.fontSize.sm, 
                            color: theme.colors.primary,
                            fontWeight: theme.typography.fontWeight.semibold,
                            marginTop: theme.spacing.xs,
                            padding: '2px 6px',
                            backgroundColor: `${theme.colors.primary}10`,
                            borderRadius: theme.borderRadius.sm,
                            display: 'inline-block',
                          }}>
                            {item.variant_name}
                          </div>
                        )}
                        <SizeLine item={item} orderStatus={selectedOrder.status} />
                        {item.selected_components && item.selected_components.length > 0 && (
                          <div style={{
                            fontSize: theme.typography.fontSize.xs,
                            color: theme.colors.textSecondary,
                            marginTop: theme.spacing.xs,
                            padding: '4px 6px',
                            backgroundColor: theme.colors.backgroundAlt,
                            borderRadius: theme.borderRadius.sm,
                            borderLeft: `2px solid ${theme.colors.accent}`,
                          }}>
                            <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: '2px' }}>
                              + Customizations:
                            </div>
                            {item.selected_components.map((comp, idx) => (
                              <div key={idx} style={{ paddingLeft: '4px' }}>
                                • {comp.name}{comp.variation_name ? ` (${comp.variation_name})` : ''}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: `${theme.spacing.lg} ${theme.spacing.md}`, color: theme.colors.textSecondary, fontFamily: 'monospace', whiteSpace: 'nowrap', fontSize: theme.typography.fontSize.sm }}>{item.sku}</td>
                      <td style={{ padding: `${theme.spacing.lg} ${theme.spacing.md}`, textAlign: 'center', fontWeight: theme.typography.fontWeight.medium, whiteSpace: 'nowrap' }}>{item.qty}</td>
                      <td style={{ padding: `${theme.spacing.lg} ${theme.spacing.md}`, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {hasDiscount ? (
                          <div>
                            <div style={{ 
                              fontSize: theme.typography.fontSize.xs,
                              color: theme.colors.textSecondary,
                              textDecoration: 'line-through',
                              marginBottom: '2px',
                            }}>
                              ${basePrice.toFixed(2)}
                            </div>
                            <div style={{ 
                              fontSize: theme.typography.fontSize.sm,
                              fontWeight: theme.typography.fontWeight.semibold,
                              color: theme.colors.primary,
                            }}>
                              ${currentPrice.toFixed(2)}
                            </div>
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 6px',
                              backgroundColor: wasOnSale ? '#dcfce7' : '#fef3c7',
                              border: wasOnSale ? '1px solid #86efac' : '1px solid #fcd34d',
                              borderRadius: theme.borderRadius.sm,
                              fontSize: '0.6rem',
                              color: wasOnSale ? '#16a34a' : '#92400e',
                              fontWeight: 600,
                              marginTop: '2px',
                            }}>
                              {wasOnSale && (
                                <span style={{
                                  backgroundColor: '#DC2626',
                                  color: '#FFF',
                                  padding: '1px 4px',
                                  borderRadius: '2px',
                                  fontSize: '0.55rem',
                                  fontWeight: 700,
                                  letterSpacing: '0.3px',
                                }}>SALE</span>
                              )}
                              {((basePrice - currentPrice) / basePrice * 100).toFixed(0)}% off
                            </div>
                          </div>
                        ) : (
                          <div style={{ color: theme.colors.textSecondary }}>
                            ${currentPrice.toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: `${theme.spacing.lg} ${theme.spacing.md}`, textAlign: 'right', fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text, whiteSpace: 'nowrap' }}>${(Number(item.subtotal) || 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-order-cards" style={{ display: 'none' }}>
            {selectedOrder.items?.map((item, index) => {
              const currentPrice = Number(item.unit_price) || 0;
              const basePrice = Number(item.base_price_without_discount) || currentPrice;
              const hasDiscount = !item.actual_sqft && Math.abs(currentPrice - basePrice) > 0.01; // measured hides are priced by size, not discounted
              const wasOnSale = item.was_on_sale === true;
              
              return (
                <div 
                  key={index} 
                  style={{
                    backgroundColor: theme.colors.surface,
                    border: `1px solid ${theme.colors.borderLight}`,
                    borderRadius: theme.borderRadius.md,
                    padding: theme.spacing.md,
                    marginBottom: theme.spacing.md,
                  }}
                >
                  <div style={{ 
                    fontWeight: theme.typography.fontWeight.bold, 
                    color: theme.colors.text,
                    marginBottom: theme.spacing.sm,
                    fontSize: theme.typography.fontSize.md,
                  }}>
                    {item.name}
                  </div>
                  
                  {item.variant_name && (
                    <div style={{ 
                      fontSize: theme.typography.fontSize.sm, 
                      color: theme.colors.primary,
                      fontWeight: theme.typography.fontWeight.semibold,
                      marginBottom: theme.spacing.sm,
                      padding: '4px 8px',
                      backgroundColor: `${theme.colors.primary}10`,
                      borderRadius: theme.borderRadius.sm,
                      display: 'inline-block',
                    }}>
                      {item.variant_name}
                    </div>
                  )}
                  <SizeLine item={item} orderStatus={selectedOrder.status} />

                  {item.selected_components && item.selected_components.length > 0 && (
                    <div style={{
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.colors.textSecondary,
                      marginBottom: theme.spacing.sm,
                      padding: '4px 6px',
                      backgroundColor: theme.colors.backgroundAlt,
                      borderRadius: theme.borderRadius.sm,
                      borderLeft: `2px solid ${theme.colors.accent}`,
                    }}>
                      <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: '2px' }}>
                        + Customizations:
                      </div>
                      {item.selected_components.map((comp, idx) => (
                        <div key={idx} style={{ paddingLeft: '4px' }}>
                          • {comp.name}{comp.variation_name ? ` (${comp.variation_name})` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: theme.spacing.sm,
                  marginTop: theme.spacing.md,
                  paddingTop: theme.spacing.md,
                  borderTop: `1px solid ${theme.colors.borderLight}`,
                }}>
                  <div>
                    <div style={{ 
                      fontSize: theme.typography.fontSize.xs, 
                      color: theme.colors.textLight,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: theme.spacing.xs,
                    }}>
                      SKU
                    </div>
                    <div style={{ 
                      fontFamily: 'monospace',
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.colors.textSecondary,
                      wordBreak: 'break-all',
                    }}>
                      {item.sku}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ 
                      fontSize: theme.typography.fontSize.xs, 
                      color: theme.colors.textLight,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: theme.spacing.xs,
                    }}>
                      Quantity
                    </div>
                    <div style={{ 
                      fontSize: theme.typography.fontSize.md,
                      fontWeight: theme.typography.fontWeight.semibold,
                      color: theme.colors.text,
                    }}>
                      {item.qty}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ 
                      fontSize: theme.typography.fontSize.xs, 
                      color: theme.colors.textLight,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: theme.spacing.xs,
                    }}>
                      Unit Price
                    </div>
                    {hasDiscount ? (
                      <div>
                        <div style={{ 
                          fontSize: theme.typography.fontSize.xs,
                          color: theme.colors.textSecondary,
                          textDecoration: 'line-through',
                          marginBottom: '2px',
                        }}>
                          ${basePrice.toFixed(2)}
                        </div>
                        <div style={{ 
                          fontSize: theme.typography.fontSize.md,
                          fontWeight: theme.typography.fontWeight.semibold,
                          color: theme.colors.primary,
                        }}>
                          ${currentPrice.toFixed(2)}
                        </div>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 6px',
                          backgroundColor: wasOnSale ? '#dcfce7' : '#fef3c7',
                          border: wasOnSale ? '1px solid #86efac' : '1px solid #fcd34d',
                          borderRadius: theme.borderRadius.sm,
                          fontSize: '0.6rem',
                          color: wasOnSale ? '#16a34a' : '#92400e',
                          fontWeight: 600,
                          marginTop: '2px',
                        }}>
                          {wasOnSale && (
                            <span style={{
                              backgroundColor: '#DC2626',
                              color: '#FFF',
                              padding: '1px 4px',
                              borderRadius: '2px',
                              fontSize: '0.55rem',
                              fontWeight: 700,
                              letterSpacing: '0.3px',
                            }}>SALE</span>
                          )}
                          {((basePrice - currentPrice) / basePrice * 100).toFixed(0)}% off
                        </div>
                      </div>
                    ) : (
                      <div style={{ 
                        fontSize: theme.typography.fontSize.md,
                        color: theme.colors.textSecondary,
                      }}>
                        ${currentPrice.toFixed(2)}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <div style={{ 
                      fontSize: theme.typography.fontSize.xs, 
                      color: theme.colors.textLight,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: theme.spacing.xs,
                    }}>
                      Total
                    </div>
                    <div style={{ 
                      fontSize: theme.typography.fontSize.lg,
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.colors.primary,
                    }}>
                      ${(Number(item.subtotal) || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Info Grid */}
        <div className="responsive-grid" style={styles.infoGrid}>
          <div>
            <h3 style={styles.infoCardTitle}>Shipping Details</h3>
            <div className="responsive-info-card" style={styles.infoCard}>
              <p style={{ fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.sm, fontSize: theme.typography.fontSize.lg }}>{selectedOrder.shipping_address?.full_name}</p>
              <div style={{ color: theme.colors.textSecondary, lineHeight: theme.typography.lineHeight.relaxed, fontFamily: theme.typography.fontFamily.secondary }}>
                <p>{selectedOrder.shipping_address?.address_line1}</p>
                {selectedOrder.shipping_address?.address_line2 && <p>{selectedOrder.shipping_address?.address_line2}</p>}
                <p>{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postal_code}</p>
                <p>{selectedOrder.shipping_address?.country}</p>
              </div>
              
              <div style={{ marginTop: theme.spacing.lg, paddingTop: theme.spacing.lg, borderTop: `1px solid ${theme.colors.borderLight}` }}>
                <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textLight, marginBottom: theme.spacing.xs }}>Method</p>
                <p style={{ fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text }}>
                  {selectedOrder.shipping_method === 'customer_pickup' ? 'Customer Pickup' : 'Standard Shipping'}
                </p>
                
                {selectedOrder.tracking_number && (
                  <div style={{ marginTop: theme.spacing.md }}>
                    <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textLight, marginBottom: theme.spacing.xs }}>Tracking Number</p>
                    <span style={{ fontFamily: 'monospace', background: theme.colors.surfaceAlt, padding: `${theme.spacing.xs} ${theme.spacing.sm}`, borderRadius: theme.borderRadius.sm, fontWeight: theme.typography.fontWeight.semibold, wordBreak: 'break-all', fontSize: theme.typography.fontSize.sm }}>
                      {selectedOrder.tracking_number}
                    </span>
                  </div>
                )}
                
                {selectedOrder.shipping_details && (
                  <div style={{ 
                    marginTop: theme.spacing.md,
                    padding: theme.spacing.md,
                    backgroundColor: '#F0F9FF',
                    border: '1px solid #BAE6FD',
                    borderRadius: theme.borderRadius.md,
                  }}>
                    <p style={{ 
                      fontSize: theme.typography.fontSize.sm, 
                      color: '#0369A1',
                      fontWeight: theme.typography.fontWeight.semibold,
                      marginBottom: theme.spacing.xs 
                    }}>
                      Shipping Notes
                    </p>
                    <p style={{ 
                      fontSize: theme.typography.fontSize.sm,
                      color: '#0C4A6E',
                      lineHeight: theme.typography.lineHeight.relaxed,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}>
                      {selectedOrder.shipping_details}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 style={styles.infoCardTitle}>Order Summary</h3>
            <div className="responsive-info-card" style={styles.infoCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing.md, color: theme.colors.textSecondary, gap: theme.spacing.md, fontFamily: theme.typography.fontFamily.secondary }}>
                <span>Subtotal</span>
                <span style={{ whiteSpace: 'nowrap' }}>${(Number(selectedOrder.subtotal) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing.md, color: theme.colors.textSecondary, gap: theme.spacing.md, fontFamily: theme.typography.fontFamily.secondary }}>
                <span>Shipping</span>
                <span style={{ whiteSpace: 'nowrap' }}>${(Number(selectedOrder.shipping_cost) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing.md, color: theme.colors.textSecondary, gap: theme.spacing.md, fontFamily: theme.typography.fontFamily.secondary }}>
                <span>Tax</span>
                <span style={{ whiteSpace: 'nowrap' }}>${(Number(selectedOrder.tax) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: theme.spacing.lg, paddingTop: theme.spacing.lg, borderTop: `1px solid ${theme.colors.borderLight}`, fontWeight: theme.typography.fontWeight.bold, fontSize: theme.typography.fontSize.xl, color: theme.colors.text, gap: theme.spacing.md }}>
                <span>Total</span>
                <span style={{ color: theme.colors.primary, whiteSpace: 'nowrap' }}>${(Number(selectedOrder.total) || 0).toFixed(2)}</span>
              </div>
            </div>
            
            <div style={{ marginTop: theme.spacing.xl }}>
              <Button 
                fullWidth 
                onClick={() => handleReorder(selectedOrder.id)}
                size="lg"
              >
                Reorder These Items
              </Button>
            </div>
          </div>
        </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={styles.container}>
        <div style={styles.sectionHeader}>
          <h2 className="responsive-section-title" style={styles.sectionTitle}>My Order History</h2>
        </div>

        {error && (
          <div style={styles.errorBox}>
            {error}
          </div>
        )}

        {orders.length === 0 ? (
          <div style={styles.emptyState}>
            <h3 style={{ fontFamily: theme.typography.fontFamily.primary, fontSize: theme.typography.fontSize['2xl'], marginBottom: theme.spacing.sm, color: '#8B4513' }}>No orders yet</h3>
            <p style={{ marginBottom: theme.spacing.xl, color: theme.colors.textSecondary, fontFamily: theme.typography.fontFamily.secondary }}>Start shopping to place your first order!</p>
            <Button onClick={() => navigate('/products')}>
              Browse Products
            </Button>
          </div>
        ) : (
          <div className="responsive-grid" style={styles.gridContainer}>
            {orders.map((order) => (
              <div 
                key={order.id} 
                className="responsive-order-card"
                style={styles.orderCard}
                onClick={() => setSelectedOrder(order)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = theme.shadows.lg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = theme.shadows.md;
                }}
              >
                <div style={styles.orderCardHeader}>
                  <div style={styles.orderIdGroup}>
                    <span style={styles.orderId}>#{order.id.toString().slice(0, 8)}...</span>
                    <span style={styles.orderDate}>
                      {new Date(order.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <span 
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusBgColor(order.status),
                      color: getStatusTextColor(order.status),
                    }}
                  >
                    {order.status?.replace(/_/g, ' ')}
                  </span>
                </div>
                
                <div style={styles.orderCardBody}>
                  <div style={styles.orderStat}>
                    <span style={styles.orderStatLabel}>Items</span>
                    <span style={styles.orderStatValue}>{order.items?.length || 0}</span>
                  </div>
                  <div style={styles.orderStat}>
                    <span style={styles.orderStatLabel}>Total</span>
                    <span style={{ ...styles.orderStatValue, color: theme.colors.primary }}>${(Number(order.total) || 0).toFixed(2)}</span>
                  </div>
                </div>

                {order.status === 'checkout_incomplete' && (
                  <div style={{
                    fontSize: theme.typography.fontSize.xs,
                    color: '#9A3412',
                    backgroundColor: '#FFF7ED',
                    border: '1px solid #F97316',
                    borderRadius: theme.borderRadius.sm,
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    marginBottom: theme.spacing.md,
                  }}>
                    Payment details incomplete — action needed
                  </div>
                )}

                <div className="responsive-btn-group" style={styles.orderCardFooter}>
                  <button 
                    style={styles.orderBtn}
                    onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.surfaceAlt;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.surface;
                    }}
                  >
                    View Details
                  </button>
                  {order.status === 'checkout_incomplete' ? (
                    <button
                      style={{ ...styles.orderBtn, backgroundColor: '#C2410C', color: '#FFF', border: 'none' }}
                      onClick={(e) => { e.stopPropagation(); navigate('/checkout'); }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#9A3412'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C2410C'; }}
                    >
                      Complete Checkout
                    </button>
                  ) : (
                  <button 
                    style={{ ...styles.orderBtn, ...styles.orderBtnReorder }}
                    onClick={(e) => { e.stopPropagation(); handleReorder(order.id); }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primaryDark;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primary;
                    }}
                  >
                    Reorder
                  </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default DashboardOrders;
